import { useCallback, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import IconButton from 'LuxKit/Button/IconButton'
import noop from 'lib/function/noop'
import type { CardOption, CardCategory } from './CardsList'
import LockedOptionsList from './LockedOptionsList'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'
import ImageCard from './ImageCard'

const ContentArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex: 1;
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories: 'locations' | 'places'
  onInfoClick?: (option: CardOption, category: CardCategory) => void
}

function ImageCardsList({
  options,
  onSelectionChange,
  select,
  disabled = false,
  selection,
  categories,
  onInfoClick = noop,
}: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const getOptionForEvent = useCallback((optionIndex: string | undefined) => {
    const indexStr = optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    return options[index]
  }, [options])

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const option = getOptionForEvent(event.currentTarget.dataset.optionIndex)
    if (option) {
      onInfoClickRef.current(option, categories)
    }
  }, [categories, getOptionForEvent])

  const handleSelectionClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }, [])

  const handleSelectionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    optionClickHandlers[index]?.()
  }, [optionClickHandlers])

  const getOptionKey = useCallback((option: CardOption, index: number) => `${option.title}-${index}`, [])

  const renderOption = useCallback(({ option, index, isSelected }: { option: CardOption; index: number; isSelected: boolean }) => {
    let selectionControl: React.ReactNode

    if (select === 'multiple') {
      selectionControl = (
        <CircleCheckboxInput
          checked={isSelected}
          disabled={disabled}
          data-option-index={index}
          onClick={handleSelectionClick}
          onChange={handleSelectionChange}
        />
      )
    } else {
      selectionControl = (
        <RadioButton
          checked={isSelected}
          disabled={disabled}
          data-option-index={index}
          onClick={handleSelectionClick}
          onChange={handleSelectionChange}
        />
      )
    }

    return (
      <SelectableCard
        role={select === 'single' ? 'radio' : 'checkbox'}
        aria-checked={isSelected}
        disabled={disabled}
        onClick={optionClickHandlers[index]}
        className={clsx('with-image', { selected: isSelected })}
      >
        <ImageCard
          option={option}
          category={categories}
          optionIndex={index}
          onClick={handleInfoClick}
        />
        <ContentArea>
          <Group direction="vertical" gap={2} fullWidth>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <BodyText variant="medium" colour="neutral-one" weight="bold">
                {option.title}
              </BodyText>
              <IconButton
                kind="tertiary"
                size="small"
                data-option-index={index}
                onClick={handleInfoClick}
              >
                <LineInfoCircleIcon />
              </IconButton>
            </Group>
            {option.description && (
              <BodyText variant="medium" colour="neutral-two">
                {option.description}
              </BodyText>
            )}
          </Group>
          {selectionControl}
        </ContentArea>
      </SelectableCard>
    )
  }, [categories, disabled, handleInfoClick, handleSelectionChange, handleSelectionClick, optionClickHandlers, select])

  return (
    <LockedOptionsList
      options={options}
      disabled={disabled}
      selectedIndices={selectedIndices}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
    />
  )
}

export default ImageCardsList
